import React, { useCallback, useRef, useState } from 'react';

import DataTable from '../../teacher_payments/shared/data_table';
import LoadFailed from '../../teacher_payments/shared/load_failed';
import TpDialog from '../../teacher_payments/shared/tp_dialog';
import PenaltyTable from '../../teacher_payments/penalties/penalty_table';
import { RowDialogs, row_dialog, useRowWrite } from '../../teacher_payments/penalties/row_dialogs';
import { useRowRefocus } from '../../teacher_payments/penalties/row_focus';
import { fetch_additional_sessions, fetch_additional_teachers } from '../../teacher_payments/api';
import { api_pay_month } from '../../teacher_payments/additional_payments/params';
import { DIRECTIVE_ACTION, ROW_ACTION } from '../../teacher_payments/penalties/params';
import { pass_state_for } from './adapters';

/*
 * PROD-7828 R6 — the session page's Change penalty modal: every teacher-payment penalty on the session
 * (all teachers), each row's actions through the Penalties tab's own dialogs, an Add prefilled with this
 * session, and the session's penalty history. view: session_view(); on_reload re-reads the session.
 * load_failed_text: the last re-read failed, so the rows, Add and history give way to the error and a retry.
 */
const RESULT_BANNER = {
  success: 'tp-banner-ok',
  warning: 'tp-banner-warn',
  info: 'tp-banner-info'
};

export const NO_PASS_STATE_TEXT =
  "This teacher's free passes for the month did not load. Close this and open it again.";

const HISTORY_COLUMNS = [
  { key: 'at_text', label: 'When' },
  { key: 'by_text', label: 'By' },
  { key: 'teacher_name', label: 'Teacher' },
  {
    key: 'action_text',
    label: 'Change',
    render: (h) => (
      <span>
        {h.action_text}
        {h.summary ? <div className="tp-card-field-label">{h.summary}</div> : null}
      </span>
    )
  },
  { key: 'reason', label: 'Note', render: (h) => h.reason || '—' }
];

const ChangePenaltyModal = ({
  view,
  teacher_uuid,
  teacher_name,
  new_request_id,
  load_failed_text = '',
  on_reload,
  on_permission,
  on_close
}) => {
  const [result, setResult] = useState(null);
  const [banner, setBanner] = useState('');
  const [highlight_uuid, setHighlight] = useState('');
  const scope_ref = useRef(null);
  const heading_ref = useRef(null);
  const refocus = useRowRefocus(scope_ref, heading_ref, load_failed_text ? null : view.rows);

  const w = useRowWrite({
    on_saved: (r) => {
      setResult(r);
      setBanner('');
      setHighlight(r.penalty && !r.removed ? r.penalty.uuid : '');
      refocus.arm();
      on_reload();
    },
    on_reload,
    on_permission
  });

  const on_action = (action, row) => {
    setBanner('');
    refocus.remember(row.uuid);
    const next = row_dialog(action, row);
    if (next) {
      w.open(next);
      return;
    }
    const state = pass_state_for(view, row);
    if (!state) {
      setBanner(NO_PASS_STATE_TEXT);
      return;
    }
    w.open({
      kind: 'pass',
      action: action === ROW_ACTION.APPLY_PASS ? DIRECTIVE_ACTION.APPLY : DIRECTIVE_ACTION.REMOVE,
      state,
      day: row.day_ist,
      teacher_name: row.teacher_name
    });
  };

  const search_teachers = useCallback(
    (q) => fetch_additional_teachers(q).then((d) => (Array.isArray(d.rows) ? d.rows : [])),
    []
  );
  const search_sessions = useCallback(
    (for_teacher, q, month) =>
      for_teacher
        ? fetch_additional_sessions({
            teacher_uuid: for_teacher,
            pay_month: api_pay_month(month),
            q
          }).then((d) => (Array.isArray(d.rows) ? d.rows : []))
        : Promise.resolve([]),
    []
  );

  // The teacher defaults to the page's and can be changed (a replacement teacher). Without a name the
  // picker would show nothing while a teacher is set, so a nameless teacher is not seeded.
  const seeded = !!(teacher_uuid && teacher_name);
  const seed = seeded
    ? {
        teacher_uuid,
        teacher_name,
        session_uuid: view.session_uuid,
        session_label: view.context_text
      }
    : {};

  const focus_heading = () => {
    if (heading_ref.current) heading_ref.current.focus({ preventScroll: true });
  };

  return (
    <>
      <TpDialog title_id="tp-sp-title" on_close={on_close}>
        <div className="tp-modal tp-modal-wide" ref={scope_ref}>
          <h4 id="tp-sp-title" ref={heading_ref} tabIndex={-1}>
            Change penalty
          </h4>
          <div className="tp-sp-ctx">{view.context_text}</div>
          {result ? (
            <div className={`tp-banner ${RESULT_BANNER[result.tone]}`} role="status">
              {result.message}
              {result.warnings.map((x) => (
                <div key={x.code}>{x.message}</div>
              ))}
              {result.issues_text ? <div>{result.issues_text}</div> : null}
            </div>
          ) : null}
          {banner ? (
            <div className="tp-banner tp-banner-error" role="alert">
              {banner}
            </div>
          ) : null}
          {load_failed_text ? (
            <LoadFailed text={load_failed_text} on_retry={on_reload} />
          ) : (
            <>
              <PenaltyTable
                rows={view.rows}
                selectable={false}
                on_action={on_action}
                highlight_uuid={highlight_uuid}
                empty_text="No teacher payment penalties on this session."
              />
              <div className="tp-sp-add">
                <button
                  type="button"
                  className="tp-btn-out"
                  onClick={() => w.open({ kind: 'add' })}
                >
                  + Add a penalty to this session
                </button>
              </div>
              <h5 className="tp-sp-sub">Penalty history</h5>
              <DataTable
                columns={HISTORY_COLUMNS}
                rows={view.history}
                rowKey={(h) => h.key}
                emptyText="No penalty changes on this session yet."
                cardTitle={(h) => (
                  <span>
                    {h.at_text} · {h.by_text}
                  </span>
                )}
              />
            </>
          )}
          <div className="tp-modal-ft">
            <button type="button" className="tp-btn-ghost" onClick={on_close}>
              Close
            </button>
          </div>
        </div>
      </TpDialog>
      <RowDialogs
        w={w}
        pay_month={view.pay_month}
        month_text={view.month_text}
        add={{
          new_request_id,
          search_teachers,
          search_sessions,
          seed,
          default_pcts: view.default_pcts
        }}
        on_focus_lost={focus_heading}
      />
    </>
  );
};

export default ChangePenaltyModal;
